// /app/frontend/src/components/admin/SmartScheduling.jsx
// Smart scheduling suggestions based on installer availability and location

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Sparkles, Calendar, Clock, MapPin, Star, User, 
  Loader2, Check, ChevronRight, Zap, TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SmartScheduling({ onSelectSlot }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    zip_code: '',
    event_type: '',
    duration_minutes: 60
  });

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date) params.append('date', filters.date);
      if (filters.zip_code) params.append('zip_code', filters.zip_code);
      if (filters.event_type) params.append('event_type', filters.event_type);
      params.append('duration_minutes', filters.duration_minutes.toString());

      const response = await axios.get(
        `${API}/api/admin/smart-scheduling/suggestions?${params.toString()}`,
        { headers }
      );
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-700';
    if (score >= 60) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  const handleSelectSlot = (slot, installer) => {
    if (onSelectSlot) {
      onSelectSlot({
        date: filters.date,
        time: slot.time_slot,
        installer_id: installer.installer_id,
        installer_name: installer.installer_name
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Smart Scheduling
            </CardTitle>
            <CardDescription>AI-powered time slot recommendations</CardDescription>
          </div>
          <Badge className="bg-purple-100 text-purple-700">
            <Zap className="h-3 w-3 mr-1" />
            Optimized
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="text-xs">Date</Label>
            <Input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
              className="h-9"
              data-testid="smart-schedule-date"
            />
          </div>
          <div>
            <Label className="text-xs">Customer ZIP Code</Label>
            <Input
              placeholder="e.g., 10001"
              value={filters.zip_code}
              onChange={(e) => setFilters(prev => ({ ...prev, zip_code: e.target.value }))}
              className="h-9"
              data-testid="smart-schedule-zip"
            />
          </div>
          <div>
            <Label className="text-xs">Service Type</Label>
            <select
              value={filters.event_type}
              onChange={(e) => setFilters(prev => ({ ...prev, event_type: e.target.value }))}
              className="w-full h-9 px-3 border rounded-md text-sm"
            >
              <option value="">Any</option>
              <option value="installation">Installation</option>
              <option value="measurement">Measurement</option>
              <option value="repair">Repair</option>
              <option value="consultation">Consultation</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button 
              onClick={fetchSuggestions} 
              disabled={loading}
              className="w-full h-9 bg-purple-600 hover:bg-purple-700"
              data-testid="find-slots-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Find Best Slots
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : suggestions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No available time slots found</p>
            <p className="text-sm mt-1">Try a different date or adjust filters</p>
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((slot) => (
              <div 
                key={slot.time_slot}
                className="border rounded-lg p-4 hover:border-purple-300 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{slot.time_slot}</h3>
                      <p className="text-sm text-gray-500">
                        {slot.available_installers} technician{slot.available_installers !== 1 ? 's' : ''} available
                      </p>
                    </div>
                  </div>
                  {slot.recommended && (
                    <Badge className="bg-green-100 text-green-700">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      Best Match
                    </Badge>
                  )}
                </div>

                {/* Recommended installer */}
                {slot.recommended && (
                  <div className="bg-gradient-to-r from-purple-50 to-green-50 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                          <User className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{slot.recommended.installer_name}</p>
                          <div className="flex items-center gap-2 text-sm">
                            {slot.recommended.avg_rating > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                                {slot.recommended.avg_rating}
                              </span>
                            )}
                            {slot.recommended.specialty_match && (
                              <Badge variant="outline" className="text-xs py-0">
                                <Check className="h-3 w-3 mr-1" />
                                Specialty Match
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getScoreColor(slot.recommended.overall_score)}>
                          Score: {slot.recommended.overall_score}
                        </Badge>
                        <Button 
                          size="sm"
                          onClick={() => handleSelectSlot(slot, slot.recommended)}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid={`select-slot-${slot.time_slot}`}
                        >
                          Select
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                    {slot.recommended.prev_job_location && (
                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        Previous job: {slot.recommended.prev_job_location}
                      </p>
                    )}
                  </div>
                )}

                {/* Other options */}
                {slot.all_options && slot.all_options.length > 1 && (
                  <div className="border-t pt-3 mt-3">
                    <p className="text-xs text-gray-500 mb-2">Other available technicians:</p>
                    <div className="flex flex-wrap gap-2">
                      {slot.all_options.slice(1).map((opt) => (
                        <button
                          key={opt.installer_id}
                          onClick={() => handleSelectSlot(slot, opt)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
                        >
                          <User className="h-3 w-3" />
                          {opt.installer_name}
                          {opt.avg_rating > 0 && (
                            <span className="flex items-center gap-0.5 text-xs text-gray-500">
                              <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                              {opt.avg_rating}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
